import { FC, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { AppIconButton } from '@client/foundation/design-system/components';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import {
  exitWorkspaceFullscreen,
  getGlobalWorkspaceFullscreen$,
  getWorkspaceFullscreen$,
  isGlobalWorkspaceFullscreen,
  isWorkspaceFullscreen,
  setGlobalWorkspaceFullscreen
} from './workspace-fullscreen.service';

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;

  const tagName = target.tagName;
  return (
    target.isContentEditable ||
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT'
  );
};

export const WorkspaceFullscreenToggle: FC = () => {
  const globalFullscreen = useObservable(
    () => getGlobalWorkspaceFullscreen$(),
    false
  );
  const workspaceFullscreen = useObservable(
    () => getWorkspaceFullscreen$(),
    false
  );

  const handleToggle = useCallback(() => {
    setGlobalWorkspaceFullscreen(!globalFullscreen);
  }, [globalFullscreen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      if (
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.repeat &&
        event.key.toLowerCase() === 'f' &&
        !isTypingTarget(event.target)
      ) {
        event.preventDefault();
        setGlobalWorkspaceFullscreen(!isGlobalWorkspaceFullscreen());
        return;
      }

      if (event.key !== 'Escape') return;
      if (!isWorkspaceFullscreen()) return;

      event.preventDefault();
      exitWorkspaceFullscreen();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: workspaceFullscreen ? 3 : 67,
        right: workspaceFullscreen ? 20 : 10,
        zIndex: 1300
      }}
    >
      <AppIconButton
        title={
          globalFullscreen
            ? 'Disable global full mode'
            : 'Enable global full mode'
        }
        tone={globalFullscreen ? 'primary' : 'ghost'}
        onClick={handleToggle}
        sx={{
          bgcolor: 'rgba(15, 17, 21, 0.68)',
          border: '1px solid rgba(255,255,255,0.2)',
          '&:hover': {
            bgcolor: 'rgba(15, 17, 21, 0.88)'
          }
        }}
      >
        {globalFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </AppIconButton>
    </Box>
  );
};
